"use client";

import React, { useState, useEffect, useCallback } from "react";
import { HeaderSection } from "@/components/shared/header-section";
import { PageContent } from "@/components/shared/page-content";
import { getOrderSortParams } from "@/utils/orderUtils";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { DataTableManual } from "../../components/shared/data-table-manual/data-table-manual";
import { columns } from "./components/columns";
import { DataTableToolbar } from "../../components/shared/data-table-manual/data-table-toolbar-manual";
import { useDrawerDispatch } from "@/context/DrawerContext";
import { Button } from "@/components/ui/button";
import CustomerGroupForm from "./CustomerGroupForm";
import { GET_CUSTOMER_GROUPS, GET_PALETTES } from "./customerGroupQueries";
import { useDeleteConfirmation } from "@/hooks/useDeleteConfirmation";
import { DELETE_CUSTOMERS_GROUPS } from "./customerGroupQueries";

export default function CustomerGroupsTable() {
  const [searchTerm, setSearchTerm] = useState("");
  const [tableData, setTableData] = useState([]);
  const [tableDataCount, setTableDataCount] = useState(0);
  const [order, setOrder] = useState([]);
  const [filters, setFilters] = React.useState<Map<string, Set<string>>>(
    new Map()
  );

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_CUSTOMERS_GROUPS,
    refetchQueries: ["GET_CUSTOMER_GROUPS"],
    messages: {
      success: "Customer groups deleted successfully",
      error: "Failed to delete customer groups",
    },
    itemName: "customer group",
  });

  const { data: { findManyFabricPalette } = {}, error: er2 } =
    useQuery(GET_PALETTES);

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const [loadCustomerGroups, { data, error, loading }] = useLazyQuery(
    GET_CUSTOMER_GROUPS,
    {
      variables: {},
      fetchPolicy: "cache-and-network",
    }
  );

  // Prepare filters and graphQL params to query data
  useEffect(() => {
    const queryFilter = Array.from(filters.entries())
      .map(([key, value]) => {
        if (!value.size) return null;
        return {
          [key]: {
            some: { name: { in: Array.from(value) } },
          },
        };
      })
      .filter(Boolean);

    console.log("queryFilter", queryFilter);

    loadCustomerGroups({
      variables: {
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        filters: queryFilter,
        orderBy: getOrderSortParams(order),
      },
    });
  }, [searchTerm, pagination, order, filters]);

  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 });
  }, [searchTerm, pagination.pageSize, order]);

  useEffect(() => {
    console.log("data useeffect", data);
    if (data) {
      setTableData(data.findManyCustomerGroup);
      setTableDataCount(data.findManyCustomerGroupCount ?? 0);
    }
  }, [data]);

  const dispatchLeft = useDrawerDispatch();
  const openAdvancedCustomerGroupForm = useCallback(
    (id: number) => {
      dispatchLeft({
        type: "OPEN_DRAWER",
        drawerComponent: CustomerGroupForm,
        onChange: null,
        data: {
          id,
        },
      });
    },
    [dispatchLeft]
  );

  const openNewCustomerGroupForm = useCallback(() => {
    dispatchLeft({
      type: "OPEN_DRAWER",
      drawerComponent: CustomerGroupForm,
      onChange: null,
      data: {
        id: null,
      },
    });
  }, [dispatchLeft]);

  return (
    <PageContent>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <HeaderSection title="Customer Groups" />
        <Button onClick={openNewCustomerGroupForm}>Create New +</Button>
      </div>
      {/* <SheetTrigger>Open</SheetTrigger> */}

      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        onRowClick={(row: any) => {
          openAdvancedCustomerGroupForm(row.id);
        }}
        isLoading={loading}
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: "Filter customer groups...",
                accessor: "name",
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [
                {
                  title: "Palette",
                  accessor: "fabric_palettes",
                  options: findManyFabricPalette?.map((group: any) => ({
                    label: group.name,
                    value: group.name,
                  })),
                },
                {
                  title: "Price List",
                  accessor: "price_list",
                  options: Array.from(
                    new Set(
                      data?.findManyCustomerGroup
                        ?.filter((group: any) => group.price_list)
                        .map((group: any) => group.price_list.name)
                    )
                  ).map((name) => ({
                    label: name,
                    value: name,
                  })),
                },
              ],
            }}
          />
        )}
      />
    </PageContent>
  );
}
